import { useQuery } from '@tanstack/react-query';
import { api, useAuth } from '@halo/web-lib';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { useState } from 'react';

interface StoreInfo {
  id: number;
  integrations: string[];
  onboardingStep: number;
  onboardedAt: string | null;
}

const STORE_PLATFORM_IDS = new Set(['shopify', 'tiktok_shop', 'amazon_seller']);
const AD_PLATFORM_IDS = new Set(['meta', 'google_ads', 'pinterest', 'tiktok_business', 'amazon_ads', 'klaviyo']);

/**
 * Non-blocking banner shown when the store completed onboarding
 * but skipped connecting store or ad platforms.
 */
export function SetupBanner() {
  const { storeId } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  const { data: store } = useQuery({
    queryKey: ['setup-banner', storeId],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreInfo[]>('/api/v1/auth/stores', { signal });
      if (!res.success || !res.data) return null;
      return res.data.find((s) => s.id === Number(storeId)) ?? null;
    },
    enabled: !!storeId,
    staleTime: 60_000,
  });

  if (dismissed || !store || !store.onboardedAt) return null;

  const integrations = store.integrations;
  const hasStore = integrations.some((i) => STORE_PLATFORM_IDS.has(i));
  const hasAds = integrations.some((i) => AD_PLATFORM_IDS.has(i));

  if (hasStore && hasAds) return null;

  const message = !hasStore && !hasAds
    ? 'Connect your platforms to unlock analytics'
    : !hasStore
      ? 'Connect a store to start tracking revenue'
      : 'Connect ad platforms to see ROAS and attribution';

  return (
    <div className="flex items-center gap-3 border-b border-primary-100 bg-primary-50/60 px-4 py-2.5 dark:border-primary-500/10 dark:bg-primary-500/5">
      <Sparkles className="size-4 shrink-0 text-primary-500" />
      <span className="flex-1 text-xs font-medium text-primary-700 dark:text-primary-400">
        {message}
      </span>
      <button
        type="button"
        onClick={() => navigate('/integrations')}
        className="inline-flex items-center gap-1 rounded-md bg-primary-500 px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-primary-600"
      >
        Complete Setup
        <ArrowRight className="size-3" />
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="rounded p-0.5 text-primary-400 transition-colors hover:text-primary-600 dark:hover:text-primary-300"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
